/**
 * <brio-media> -- progressive media player wrapping <audio> or <video>.
 *
 * Markup:
 *   <brio-media>
 *     <video src="..." poster="..." controls></video>
 *   </brio-media>
 *
 *   <brio-media>
 *     <audio src="..." controls></audio>
 *   </brio-media>
 *
 * Without JS, the child media keeps its native `controls` attribute
 * and the browser renders UA chrome -- the page is functional with
 * no upgrade. With JS, brio-media strips `controls` and builds a
 * controls bar of media-specific atoms (.brio-media-play,
 * .brio-media-timeline, etc.). They are not .button / .input -- a
 * transport bar communicates continuous playback state, not discrete
 * input commands, so it gets its own visual rhetoric. Each atom
 * styles itself directly off brio-media.css's host token set.
 *
 * Behavior:
 *   - Strips the child media's `controls` attribute on connect
 *     (we own UI now; native chrome would double up).
 *   - Builds the controls bar: play/pause, timeline, time display,
 *     mute, volume. Adds a fullscreen button for video only,
 *     gated on `instanceof HTMLVideoElement`.
 *   - Listens on the media for state changes (play, pause,
 *     timeupdate, loadedmetadata, durationchange, ended,
 *     volumechange) and updates the UI to match.
 *   - Listens on user input (button clicks, range input) and drives
 *     the HTMLMediaElement API (play, pause, currentTime, volume,
 *     muted, requestFullscreen).
 *   - Toggles between play/pause icons (and mute/unmute, fullscreen
 *     enter/exit) via [hidden] on child SVGs -- no innerHTML
 *     swaps after the initial build.
 *   - Fullscreen requests target the host <brio-media>, not the
 *     inner <video>, so the controls bar stays visible in
 *     fullscreen.
 *
 * Listener lifecycle:
 *   AbortController is created in connectedCallback and aborted in
 *   disconnectedCallback. Every addEventListener (on the media,
 *   on the buttons, on the ranges, on document for
 *   fullscreenchange) passes { signal: ac.signal }, so a single
 *   ac.abort() removes them all without per-listener bookkeeping.
 *
 * Overlay variant (`overlay` attribute on the host, video only):
 *   The controls bar floats absolutely on top of the media (CSS-
 *   driven) and recolors light-on-dark for legibility regardless of
 *   theme. This element layers an idle auto-hide on top: a setTimeout
 *   fires IDLE_DELAY_MS after the most recent activity (pointer
 *   movement, focus change, tap on the video surface) while playback
 *   is in progress, adding an `idle` attribute to the host. Pause /
 *   ended cancel the timer and remove `idle`. Audio silently ignores
 *   the variant -- the inline transport already serves the same
 *   intent and there is no surface to fade against.
 *
 * Modifier API (host attributes, all bare):
 *   overlay        Opt into the floating-controls variant (video).
 *   idle           JS-applied while playing + idle; CSS fades the bar.
 *   no-volume      Suppress the mute button and volume slider.
 *   no-fullscreen  Suppress the fullscreen button (video only).
 *
 *   brio-media is a custom element and owns its attribute namespace,
 *   so the modifiers don't carry a data-* prefix or live as classes.
 *
 * Range progress fill:
 *   WebKit has no native filled-portion pseudo for `input[type=
 *   "range"]`. brio-media.css renders the fill via a linear-gradient on
 *   the track, stopped at a CSS custom property `--value` (0-100).
 *   This element is the only writer of that property; it updates
 *   `--value` whenever it sets `.value` on the timeline or volume
 *   range. Firefox uses native `::-moz-range-progress` and ignores
 *   `--value` harmlessly.
 */

import { BrioBase } from '../brio-base.js';

// Inline SVG icon constants. 14px outer size with a 24-unit
// viewBox. Filled glyphs (play, pause) use fill:currentColor;
// stroked glyphs (volume, mute, fullscreen) match the
// stroke:currentColor stroke-width:2 style of the icons in
// brio-code-example. The .button > svg sizing rule in button.css
// will pick the inline 14x14 up via SVG intrinsic.

const ICON_PLAY =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><polygon points="6 3 20 12 6 21 6 3"/></svg>';

const ICON_PAUSE =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';

const ICON_VOLUME =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>';

const ICON_MUTE =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="22" y1="9" x2="16" y2="15"/><line x1="16" y1="9" x2="22" y2="15"/></svg>';

const ICON_FULLSCREEN_ENTER =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>';

const ICON_FULLSCREEN_EXIT =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>';

// Idle delay before the `idle` attribute is applied to the overlay host (video
// only). Matches the convention of native video apps -- ~3 seconds is
// long enough that brief pauses-between-mouse-moves don't flicker the
// bar, short enough that the chrome doesn't linger.
const IDLE_DELAY_MS = 3000;

class BrioMedia extends BrioBase {
    static tag = 'brio-media';
    static {
        customElements.define(BrioMedia.tag, BrioMedia);
    }

    #media;
    #isVideo;
    #isOverlay;
    #ac;
    #idleTimer = 0;

    #playBtn;
    #playIcon;
    #pauseIcon;
    #timeline;
    #timeDisplay;
    #muteBtn;
    #volumeIcon;
    #muteIcon;
    #volume;
    #fullscreenBtn;
    #fullscreenEnterIcon;
    #fullscreenExitIcon;

    connectedCallback() {
        this.#media = this.$('audio, video');
        if (!this.#media) return;

        this.#isVideo = this.#media instanceof HTMLVideoElement;
        this.#media.removeAttribute('controls');

        this.#ac = new AbortController();
        const signal = this.#ac.signal;

        this.#buildControls();
        this.#wireMediaEvents(signal);
        this.#wireUserEvents(signal);

        // Overlay variant adds idle auto-hide on top of the standard
        // wiring. Video-only -- audio overlay is purely visual (no
        // surface to fade against).
        this.#isOverlay = this.hasAttribute('overlay');
        if (this.#isOverlay && this.#isVideo) {
            this.#wireIdleEvents(signal);
        }

        // The media may already be past HAVE_METADATA by the time
        // the element connects (browser cache, preload="auto", or
        // a connect that fires after a teardown/reattach). Re-read
        // current state so the UI reflects reality instead of the
        // initial defaults from the just-built DOM.
        this.#syncFromMedia();
    }

    disconnectedCallback() {
        if (this.#ac) this.#ac.abort();
    }

    /* --- Build phase --------------------------------------------------- */

    #setHidden(el, hide) {
        if (hide) el.setAttribute('hidden', '');
        else el.removeAttribute('hidden');
    }

    #buildControls() {
        const bar = document.createElement('div');
        bar.className = 'brio-media-controls';

        this.#playBtn = this.#makeIconButton(ICON_PLAY + ICON_PAUSE, 'Play', 'brio-media-play');
        [this.#playIcon, this.#pauseIcon] = this.#playBtn.querySelectorAll('svg');
        this.#setHidden(this.#pauseIcon, true);

        this.#timeline = this.#makeRange('brio-media-timeline', 'Seek', 0, 100, 'any', 0);
        this.#timeline.disabled = true;

        this.#timeDisplay = document.createElement('span');
        this.#timeDisplay.className = 'brio-media-time';
        this.#timeDisplay.textContent = '0:00 / 0:00';

        bar.append(this.#playBtn, this.#timeline, this.#timeDisplay);

        if (!this.hasAttribute('no-volume')) {
            this.#muteBtn = this.#makeIconButton(
                ICON_VOLUME + ICON_MUTE,
                'Mute',
                'brio-media-mute'
            );
            [this.#volumeIcon, this.#muteIcon] = this.#muteBtn.querySelectorAll('svg');
            this.#setHidden(this.#muteIcon, true);

            this.#volume = this.#makeRange(
                'brio-media-volume',
                'Volume',
                0,
                1,
                '0.01',
                this.#media.volume
            );

            bar.append(this.#muteBtn, this.#volume);
        }

        if (this.#isVideo && !this.hasAttribute('no-fullscreen')) {
            this.#fullscreenBtn = this.#makeIconButton(
                ICON_FULLSCREEN_ENTER + ICON_FULLSCREEN_EXIT,
                'Enter fullscreen',
                'brio-media-fullscreen'
            );
            [this.#fullscreenEnterIcon, this.#fullscreenExitIcon] =
                this.#fullscreenBtn.querySelectorAll('svg');
            this.#setHidden(this.#fullscreenExitIcon, true);
            bar.append(this.#fullscreenBtn);
        }

        this.append(bar);
    }

    #makeIconButton(iconHTML, ariaLabel, mediaClass) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = mediaClass;
        btn.setAttribute('aria-label', ariaLabel);
        btn.innerHTML = iconHTML;
        return btn;
    }

    #makeRange(mediaClass, ariaLabel, min, max, step, value) {
        const r = document.createElement('input');
        r.type = 'range';
        r.className = mediaClass;
        r.min = String(min);
        r.max = String(max);
        r.step = String(step);
        r.value = String(value);
        r.setAttribute('aria-label', ariaLabel);
        this.#setRangeFill(r);
        return r;
    }

    /* Set --value (0-100) so brio-media.css's WebKit track gradient draws
       the filled portion correctly. Read directly from the element so
       the helper works for both the timeline (max = duration in
       seconds) and the volume range (max = 1) without per-call
       arithmetic at every call site. */
    #setRangeFill(rangeEl) {
        const v = Number(rangeEl.value);
        const max = Number(rangeEl.max);
        const pct = max > 0 ? (v / max) * 100 : 0;
        rangeEl.style.setProperty('--value', String(pct));
    }

    /* --- Event wiring -------------------------------------------------- */

    #wireMediaEvents(signal) {
        const m = this.#media;
        const opts = { signal };

        // loadedmetadata fires once duration is known. durationchange
        // covers cases where duration changes after load (live
        // streams that estimate then refine, or src changes
        // mid-life).
        m.addEventListener('loadedmetadata', () => this.#updateTime(), opts);
        m.addEventListener('durationchange', () => this.#updateTime(), opts);

        // timeupdate fires every ~250ms while playing. Throttling
        // is unnecessary -- the browser's internal cadence is
        // already coarse and we're updating two DOM nodes.
        m.addEventListener('timeupdate', () => this.#updateTime(), opts);

        m.addEventListener('play', () => this.#renderPlayState(), opts);
        m.addEventListener('pause', () => this.#renderPlayState(), opts);
        m.addEventListener('ended', () => this.#renderPlayState(), opts);

        if (this.#muteBtn) {
            m.addEventListener('volumechange', () => this.#renderMuteState(), opts);
        }

        // fullscreenchange fires on document, not on the element.
        // The signal in opts ensures this doc-level listener is
        // also removed when the component disconnects.
        if (this.#fullscreenBtn) {
            document.addEventListener(
                'fullscreenchange',
                () => this.#renderFullscreenState(),
                opts
            );
        }
    }

    #wireUserEvents(signal) {
        const opts = { signal };

        this.#playBtn.addEventListener('click', () => this.#togglePlay(), opts);

        // Range "input" event fires on every value change while
        // the user drags; "change" only fires on release. We want
        // continuous feedback for both timeline scrubbing and
        // volume adjust, so we listen on "input".
        this.#timeline.addEventListener('input', () => this.#seek(), opts);

        if (this.#muteBtn) {
            this.#muteBtn.addEventListener('click', () => this.#toggleMute(), opts);
        }
        if (this.#volume) {
            this.#volume.addEventListener('input', () => this.#setVolume(), opts);
        }

        if (this.#fullscreenBtn) {
            this.#fullscreenBtn.addEventListener('click', () => this.#toggleFullscreen(), opts);
        }
    }

    /* --- Idle auto-hide (overlay video only) --------------------------- */

    #wireIdleEvents(signal) {
        const opts = { signal };

        // pointermove fires on mouse drag and during finger drag, so it
        // covers both desktop hover and active touch. Each move resets
        // the idle timer.
        this.addEventListener('pointermove', () => this.#showControls(), opts);

        // Single tap on the host or video surface (not on a control
        // button) counts as activity. Filtering by target keeps clicks
        // on play/mute/etc. from short-circuiting their own handlers
        // -- the bubbling pointerdown still reaches them.
        this.addEventListener(
            'pointerdown',
            (e) => {
                if (e.target === this || e.target === this.#media) {
                    this.#showControls();
                }
            },
            opts
        );

        // Pointer left the host -- definitive "not interacting" signal.
        // Schedule the hide; the helper no-ops if not playing.
        this.addEventListener('pointerleave', () => this.#scheduleHide(), opts);

        // Keyboard activity inside the host. Focus-in keeps the bar
        // visible regardless of pointer state; focus-out re-arms the
        // timer (only effective while playing).
        this.addEventListener('focusin', () => this.#showControls(), opts);
        this.addEventListener('focusout', () => this.#scheduleHide(), opts);

        // Media-driven transitions -- start the timer when playback
        // begins, cancel it on pause / ended so the bar stays visible.
        this.#media.addEventListener('play', () => this.#scheduleHide(), opts);
        this.#media.addEventListener('pause', () => this.#showControls(), opts);
        this.#media.addEventListener('ended', () => this.#showControls(), opts);

        // Element may attach mid-playback (cached video, autoplay, or
        // detach/reattach during play). The "play" event already fired
        // in those cases, so seed the timer manually.
        if (!this.#media.paused) this.#scheduleHide();
    }

    #showControls() {
        if (this.#idleTimer) {
            clearTimeout(this.#idleTimer);
            this.#idleTimer = 0;
        }
        this.removeAttribute('idle');
    }

    #hideControls() {
        // Paused / ended state always shows the bar. The timer can fire
        // after a pause if it was set just before -- guard here so the
        // class only goes on while actually playing.
        if (!this.#media.paused && !this.#media.ended) {
            this.setAttribute('idle', '');
        }
        this.#idleTimer = 0;
    }

    #scheduleHide() {
        if (this.#media.paused || this.#media.ended) return;
        if (this.#idleTimer) clearTimeout(this.#idleTimer);
        this.#idleTimer = setTimeout(() => this.#hideControls(), IDLE_DELAY_MS);
    }

    /* --- Actions ------------------------------------------------------- */

    #togglePlay() {
        if (this.#media.paused) {
            // play() returns a promise that can reject (autoplay
            // policy, media not ready, etc.). The play state
            // listener won't fire on rejection, so the UI stays
            // in the paused render -- which is the correct visual
            // feedback. Swallow the rejection so it doesn't
            // surface as an unhandled promise.
            this.#media.play().catch(() => {});
        } else {
            this.#media.pause();
        }
    }

    #toggleMute() {
        this.#media.muted = !this.#media.muted;
    }

    async #toggleFullscreen() {
        if (document.fullscreenElement === this) {
            await document.exitFullscreen().catch(() => {});
        } else {
            // Fullscreen the host element (not the inner <video>)
            // so the controls bar stays visible. requestFullscreen
            // can reject (permission denied, unsupported); same
            // rationale as #togglePlay -- swallow the rejection
            // and let the existing UI state stand.
            await this.requestFullscreen().catch(() => {});
        }
    }

    #seek() {
        // After loadedmetadata, the timeline's max is set to the
        // media's duration in seconds, so the range value IS the
        // currentTime in seconds.
        this.#media.currentTime = Number(this.#timeline.value);
        // Reflect the drag immediately on WebKit -- the timeupdate
        // event lags the input event by one tick, which would
        // flicker the gradient track during fast scrubbing.
        this.#setRangeFill(this.#timeline);
    }

    #setVolume() {
        const v = Number(this.#volume.value);
        this.#media.volume = v;
        this.#setRangeFill(this.#volume);

        // Drag-to-unmute -- if the user pulls the volume up while
        // muted, unmute. Matches the convention of every native
        // media player.
        if (v > 0 && this.#media.muted) {
            this.#media.muted = false;
        }
    }

    /* --- Render functions --------------------------------------------- */

    // Each render function reads from the media and writes to the
    // UI; they're idempotent so we can call them from event
    // handlers or from the initial sync without re-implementing
    // the logic.

    #updateTime() {
        const cur = this.#media.currentTime;
        const dur = this.#media.duration;

        if (isFinite(dur) && dur > 0) {
            this.#timeline.max = String(dur);
            this.#timeline.value = String(cur);
            this.#timeline.disabled = false;
        } else {
            // Live stream or unknown duration -- disable seeking.
            this.#timeline.disabled = true;
        }

        this.#setRangeFill(this.#timeline);
        this.#timeDisplay.textContent = `${formatTime(cur)} / ${formatTime(dur)}`;
    }

    #renderPlayState() {
        const playing = !this.#media.paused && !this.#media.ended;
        this.#setHidden(this.#playIcon, playing);
        this.#setHidden(this.#pauseIcon, !playing);
        this.#playBtn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
    }

    #renderMuteState() {
        if (!this.#muteBtn) return;
        const muted = this.#media.muted || this.#media.volume === 0;
        this.#setHidden(this.#volumeIcon, muted);
        this.#setHidden(this.#muteIcon, !muted);
        this.#muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');

        // Reflect actual volume on the slider (covers programmatic
        // changes via media.volume, and the unmute-on-drag flow
        // in #setVolume).
        this.#volume.value = String(this.#media.muted ? 0 : this.#media.volume);
        this.#setRangeFill(this.#volume);
    }

    #renderFullscreenState() {
        if (!this.#fullscreenBtn) return;
        const isFullscreen = document.fullscreenElement === this;
        this.#setHidden(this.#fullscreenEnterIcon, isFullscreen);
        this.#setHidden(this.#fullscreenExitIcon, !isFullscreen);
        this.#fullscreenBtn.setAttribute(
            'aria-label',
            isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'
        );
    }

    /* --- Initial sync -------------------------------------------------- */

    // If the media is already past HAVE_METADATA when we connect,
    // run the time render directly (the loadedmetadata event
    // already fired). HAVE_METADATA = readyState >= 1.
    #syncFromMedia() {
        if (this.#media.readyState >= 1) {
            this.#updateTime();
        }
        this.#renderPlayState();
        this.#renderMuteState();
        this.#renderFullscreenState();
    }
}

/**
 * Format a duration (seconds) as M:SS or H:MM:SS for the time
 * display. Returns "0:00" for non-finite or negative inputs --
 * Infinity for live streams, NaN before metadata is loaded.
 */
function formatTime(sec) {
    if (!isFinite(sec) || sec < 0) return '0:00';
    const hours = Math.floor(sec / 3600);
    const mins = Math.floor((sec % 3600) / 60);
    const secs = Math.floor(sec % 60);
    if (hours > 0) {
        return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${mins}:${String(secs).padStart(2, '0')}`;
}
